import { API, APIResponse, APIPaginatedResponse } from '../types';
import { action } from 'mobx';
import {AggregatedTagSchema, ArtistSchema, PlaylistSchema, ReleaseSchema, TrackSchema} from '../../../types/schema';
import getMappedMoodName from '../../../lib/getMappedMoodName';

export class BrowseAPIController extends API {
  @action
  getRecentlyAddedTracks = async () => {
    const url = `/catalog/playlist/hookd-just-added/tracks`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<TrackSchema>>;

    this.cache.addTracks(json.data);

    return json;
  };

  @action
  getRecentlyAddedArtists = async () => {
    const url = `/catalog/browse/recently-added/artists`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<ArtistSchema>>;

    this.cache.addArtists(json.data);

    return json;
  };

  @action
  getRecentlyAddedReleases = async () => {
    const url = `/catalog/browse/recently-added/releases`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<ReleaseSchema>>;

    return json;
  };

  @action
  getLeavingSoonTracks = async () => {
    const url = `/catalog/browse/leaving-soon`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<TrackSchema>>;

    this.cache.addTracks(json.data);

    return json;
  };

  @action
  getFeaturedTracks = async () => {
    const url = `/catalog/playlist/hookd-remixes/tracks`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<TrackSchema>>;

    this.cache.addTracks(json.data);

    return json;
  };

  @action
  getFeaturedArtists = async () => {
    const url = `/catalog/browse/featured/artists`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<ArtistSchema>>;

    this.cache.addArtists(json.data);

    return json;
  };

  @action
  getGenreTracks = async (slug: string, page: number = 1) => {
    const url = `/catalog/tag/hookd_genre/${slug}/tracks?page=${page}`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIPaginatedResponse<Array<TrackSchema>>;

    this.cache.addTracks(json.data);

    return json;
  };

  @action
  getPlaylists = async () => {
    const url = `/catalog/playlist`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<PlaylistSchema>>;

    return json;
  };

  @action
  getBrowsePlaylists = async () => {
      const url = `/catalog/playlist/browse`;
      const resp = await this.request.get({ url });
      const json = await resp.json() as APIResponse<Array<PlaylistSchema>>;

      return json;
  };

  @action
  getPlaylist = async (slug: string) => {
    try {
      const url = `/catalog/playlist/${slug}`;
      const resp = await this.request.get({url});
      const json = await resp.json() as APIResponse<PlaylistSchema>;

      return json;
    } catch (e) {
      return {} as APIResponse<PlaylistSchema>;
    }
  };

  @action
  getPlaylistTracks = async (slug: string, page: number = 1) => {
    try {
      const url = `/catalog/playlist/${slug}/tracks?page=${page}`;
      const resp = await this.request.get({url});
      const json = await resp.json() as APIPaginatedResponse<Array<TrackSchema>>;
      
      this.cache.addTracks(json.data);

      return json;
    } catch (e) {
      return {} as APIPaginatedResponse<Array<TrackSchema>>;
    }
  };

  @action
  getArtistList = async (slug: string) => {
    const url = `/catalog/artist/list/${slug}`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIPaginatedResponse<Array<ArtistSchema>>;

    this.cache.addArtists(json.data);

    return json;
  };

  @action
  getSystemTags = async (type: string, onlyListed = true): Promise<APIPaginatedResponse<Array<AggregatedTagSchema>>> => {
    const tags = await this.getTags('hookd', type, onlyListed);

    // todo remove temporary override after backend supports emoji
    if (type === 'hookd_mood') {
      tags.data = tags.data.map(t => ({...t, tag: getMappedMoodName(t.tag, t.slug)}))
    }

    return tags;
  };

  @action
  getTags = async (category: string, type: string, onlyListed = true): Promise<APIPaginatedResponse<Array<AggregatedTagSchema>>> => {
    const url = `/catalog/tag/${category}/${type}/tags${onlyListed ? `?is_listed=1` : ''}`;
    const resp = await this.request.get({url});

    return await resp.json();
  };

  @action
  getRightsholderReleases = async (slug: string) => {
    const url = `/catalog/rightsholder/${slug}/releases?perPage=12&includes=images`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<ReleaseSchema>>;

    return json;
  };
}